import React from "react";
import { Link } from "react-router-dom";
import { Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

import NavBar from "@/components/NavBar";
import ParticleBackground from "@/components/ParticleBackground";
import HeroDM from "@/components/subscription/sales-v2/HeroDM";
import IdeaBlock from "@/components/subscription/sales-v2/IdeaBlock";
import RepliesSection from "@/components/subscription/sales-v2/RepliesSection";
import FounderSection from "@/components/subscription/sales-v2/FounderSection";
import ToolsSection from "@/components/subscription/sales-v2/ToolsSection";
import PricingBand from "@/components/subscription/sales-v2/PricingBand";
import FinalCTABand from "@/components/subscription/sales-v2/FinalCTABand";

const Subscription: React.FC = () => {
  const { subscribed, tier, subscriptionEnd } = useSubscription();
  const { toast } = useToast();

  const handleManageSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to open subscription management.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden">
      <ParticleBackground />
      <NavBar />

      {subscribed && (
        <div className="fixed top-4 right-4 z-[60]">
          <Button
            variant="outline"
            size="sm"
            onClick={handleManageSubscription}
            className="cosmic-button-outline text-xs"
            title={
              subscriptionEnd
                ? `Renews ${new Date(subscriptionEnd).toLocaleDateString()}`
                : undefined
            }
          >
            <Settings className="h-4 w-4 mr-2" />
            Manage
          </Button>
        </div>
      )}

      <main>
        <HeroDM />

        <IdeaBlock
          id="idea"
          eyebrow="The diagnosis"
          statement={
            <>
              You haven't lost it.
              <br />
              You've just stopped <span className="text-cosmic-gradient">feeling</span> it.
            </>
          }
        >
          <p>
            Life got busy, then it got flat. The colour drained out a little. You're moving
            through the days, ticking the boxes, but that old zest, the sense of being fully{" "}
            <i>alive</i>, went somewhere you can't quite reach.
          </p>
          <p>
            So you do what we all do. You tell yourself: <i>when</i> I have the home, the
            relationship, the work, the freedom, <i>then</i> I'll feel it again. You put the
            feeling on layaway, waiting for life to hand it over first.
          </p>
        </IdeaBlock>

        <IdeaBlock
          tint
          eyebrow="The reframe"
          statement={
            <>
              But the feeling was never on the other side of the thing.
              <br />
              It was always <span className="text-cosmic-gradient">available now.</span>
            </>
          }
        >
          <p>
            Here's the quiet truth at the centre of all of this: the feeling you think you'll
            finally have <i>once</i> you've got the dream, that exact feeling can be felt{" "}
            <b className="text-white">today</b>. And when it's felt today, the reality of those
            things doesn't lag behind. It <b className="text-white">follows</b>.
          </p>
          <p>
            Everyone who has ever built the life they dreamed of will tell you the same thing:
            they believed it, and felt it as true, <i>before</i> it ever showed up. Wanting is a
            declaration that you don't have it. Feeling it real is a declaration that you do.
          </p>
        </IdeaBlock>

        <RepliesSection />

        <IdeaBlock
          eyebrow="The missing piece"
          statement={
            <>
              We were always told <span className="text-cosmic-gradient">that</span> we could.
              <br />
              We were never shown <span className="text-cosmic-gradient">how.</span>
            </>
          }
        >
          <p>
            The great books got us halfway. <i>The Secret</i> and a hundred teachers after it
            told us, beautifully, that we create our own reality. And they were right. But they
            handed us the law and stopped at the doorstep, long on <i>what's true</i>, short on
            the practical tools to actually live it, day after day, until it sinks past the
            thinking mind and becomes the way you feel.
          </p>
          <p>
            Knowing the law isn't the same as practising it. You can read every word and still
            wake up tomorrow feeling flat. What's been missing all along is the tool to{" "}
            <b className="text-white">do</b> it, to step into the feeling, see it, hold it, and
            return to it every single morning.
          </p>
        </IdeaBlock>

        <FounderSection />

        <ToolsSection />

        <PricingBand currentTier={tier} subscribed={subscribed} />

        <FinalCTABand />
      </main>

      <footer className="border-t border-white/10 py-6 text-center text-sm text-white/50 relative z-10">
        <div className="container">
          <p>Mindmail © {new Date().getFullYear()} · Your personal manifestation coach</p>
        </div>
      </footer>
    </div>
  );
};

export default Subscription;
